const _excluded = ["defaultValue", "onChange", "renderEndSlot", "renderInput", "renderStartSlot", "value", "clearTextLabel"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import * as React from "react";
import Search from "@bolteu/kalep-react-icons/dist/Search";
import { cx } from "../../helpers/utils/cx";
import { mergeRefs } from "../../helpers/utils/mergeRefs";
import { ClearInputButton } from "../_internal/ClearInputButton";
import { BaseTextField } from "./BaseTextField";
export const DEFAULT_CLEAR_TEXT_ARIA_LABEL = "Clear";
export const SearchTextField = /*#__PURE__*/React.forwardRef((props, forwardedRef) => {
  const {
      defaultValue,
      onChange,
      renderEndSlot: renderEndSlotProp,
      renderInput: renderInputProp,
      renderStartSlot: renderStartSlotProp,
      value,
      clearTextLabel = DEFAULT_CLEAR_TEXT_ARIA_LABEL
    } = props,
    textFieldBaseProps = _objectWithoutProperties(props, _excluded);
  const internalRef = React.useRef();
  const [isClearButtonVisible, setIsClearButtonVisible] = React.useState(Boolean(defaultValue || value));

  /**
   * Needed for showing/hiding clear button based on current value.
   * Then just pass through to the provided onChange prop.
   */
  const handleChange = React.useCallback(event => {
    setIsClearButtonVisible(event.target.value.length > 0);
    onChange?.(event);
  }, [onChange]);

  /**
   * Fancy way to clear the value so that the fact of clearing registers as a change event.
   */
  const handleClearButtonClick = React.useCallback(() => {
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
    nativeInputValueSetter?.call(internalRef.current, "");
    internalRef?.current?.dispatchEvent(new Event("input", {
      bubbles: true
    }));
    internalRef?.current?.focus();
  }, [internalRef]);
  const renderEndSlot = React.useCallback(function endSlotRenderer(renderProps) {
    function renderEndSlotDefault(propsForRender) {
      if (!isClearButtonVisible || propsForRender.disabled) {
        return null;
      }
      return /*#__PURE__*/React.createElement(ClearInputButton, {
        clearTextLabel: propsForRender.clearTextLabel,
        onClick: handleClearButtonClick,
        size: propsForRender.size === "sm" ? "sm" : "lg"
      });
    }
    return renderEndSlotProp ? renderEndSlotProp(renderProps, renderEndSlotDefault) : renderEndSlotDefault(renderProps);
  }, [handleClearButtonClick, isClearButtonVisible, renderEndSlotProp]);
  const renderStartSlot = React.useCallback(function startSlotRenderer(renderProps) {
    return renderStartSlotProp ? renderStartSlotProp(renderProps, renderStartSlotDefault) : renderStartSlotDefault(renderProps);
  }, [renderStartSlotProp]);
  const renderInput = React.useCallback(function inputRenderer(renderProps, renderInputBaseDefault) {
    function renderInputDefault(localProps) {
      const DefaultInput = renderInputBaseDefault(localProps);
      return /*#__PURE__*/React.cloneElement(DefaultInput, {
        className: `${DefaultInput.props.className} ${cx("kalep-search-textfield")}`
      });
    }
    return renderInputProp ? renderInputProp(renderProps, renderInputBaseDefault) : renderInputDefault(renderProps);
  }, [renderInputProp]);
  return /*#__PURE__*/React.createElement(BaseTextField, _extends({
    defaultValue: defaultValue,
    onChange: handleChange,
    renderEndSlot: renderEndSlot,
    renderInput: renderInput,
    renderStartSlot: renderStartSlot,
    value: value,
    ref: mergeRefs([internalRef, forwardedRef]),
    clearTextLabel: clearTextLabel
  }, textFieldBaseProps));
});
function renderStartSlotDefault(_ref) {
  let {
    size
  } = _ref;
  return /*#__PURE__*/React.createElement(Search, {
    "aria-hidden": "true",
    className: cx("flex-none"),
    size: size === "sm" ? "sm" : "lg"
  });
}